import { useEffect, useRef, useState, useCallback } from "react";
import axios from "../../Utils/axiosInstance";
import { searchProductVarieties } from "../../Utils/ProductSearchUtils";

export const useProductVarietiesCache = () => {
  const [varieties, setVarieties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const cacheRef = useRef(null);
  const isFetchingRef = useRef(false);

  const fetchAllVarieties = async () => {
    if (cacheRef.current) {
      setVarieties(cacheRef.current);
      return cacheRef.current;
    }
    if (isFetchingRef.current) return null;

    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);
      const { data } = await axios.get("/product-variety/get-all", {
        params: { populate: "true", isActive: "true" },
      });
      const fetchedVarieties = data.data || [];
      cacheRef.current = fetchedVarieties;
      setVarieties(fetchedVarieties);
      return fetchedVarieties;
    } catch (err) {
      console.error("Error fetching product varieties:", err);
      setError(err.message || "Failed to fetch varieties");
      setVarieties([]);
      return null;
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  };

  // ✅ THE FIX: wrap in useCallback with empty deps — cacheRef is a ref so
  // it never changes reference, making [] safe here
  const search = useCallback((query) => {
    if (!query || !query.trim()) {
      setSearchResults([]);
      return [];
    }
    if (!cacheRef.current || cacheRef.current.length === 0) {
      setSearchResults([]);
      return [];
    }
    const results = searchProductVarieties(query, cacheRef.current);
    setSearchResults(results);
    return results;
  }, []); // ✅ stable forever — cacheRef.current is accessed at call time, not captured

  useEffect(() => {
    fetchAllVarieties();
  }, []);

  return {
    varieties,
    loading,
    error,
    searchResults,
    search,
    refetch: fetchAllVarieties,
    isInitialized: cacheRef.current !== null,
  };
};

export default useProductVarietiesCache;
